import produce from 'immer';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Notation, NucleotideType} from 'biojs/BioConstants';
import {MonomerMapping} from 'bulk-import/panes/MonomerMappingForm';
import {COLUMN_TYPE as SpreadsheetBulkImportColumnType} from 'bulk-import/SpreadsheetBulkImportConstants';
import {SEQUENCE_MAX_LENGTH, SequenceType} from 'constants/SequenceConstants';
import {SequenceFeature} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getUTFlexibleNaturalBasesIfEnabled} from 'modbio/util';
import areAminoAcidsValid from 'molbio/aa-sequences/util/areAminoAcidsValid';
import {BioentityQuery} from 'notebook/integrations/registry/__generated/BioentityQuery';
import {
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
  RegistrationTableInsertType,
} from 'notebook/integrations/registry/RegistrationTableConstants';
import {
  BASES_HELM_COLUMN_NAME,
  BASES_IDT_COLUMN_NAME,
  BASES_COLUMN_NAME,
} from 'notebook/registration-tables/RegistrationTableUtil';
import {BulkBioentityLiveDataQuery} from 'notebook/structured-tables/row-updaters/__generated/BulkBioentityLiveDataQuery';
import {ResolutionQuery} from 'notebook/tables/excess/Resolver';
import {ValueError} from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  BaseTypedValueType,
  CustomEvalResult,
  TableColumnType,
  TableOpFunction,
  TableOpOptions,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {
  ResolveQueryFn,
  makeTypedValueFromCustomResolvedValue,
  ResolvedValueInfo,
  resolveHELMOrIDTValue,
  resolveNaturalBasesValue,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {ellipsize} from 'util/NameUtil';

import {
  BasesColumnDefinition,
  ColumnDefinition,
  getColumnTypeFromColumnOrDefinition,
  isColumnDefinition,
} from '../columns/ColumnDefinitions';
import {RegisteredEntityInfo} from '../row-updaters/RegistrationTableRowUpdater';
import {BasesStructuredTableColumn, StructuredTableColumn} from '../StructuredTableConstants';

type BioentityQueryBasicFolderItemNode = ExtractByTypename<
  BioentityQuery['directBioentity'],
  'BasicFolderItemNode'
>;

type BioentityQueryMixtureNode = ExtractByTypename<BioentityQuery['directBioentity'], 'MixtureNode'>;

type BioentityQueryProteinNode = ExtractByTypename<BioentityQuery['directBioentity'], 'ProteinNode'>;

type BioentityQuerySequenceNode = ExtractByTypename<BioentityQuery['directBioentity'], 'SequenceNode'>;

type BioentityQueryMoleculeNode = ExtractByTypename<BioentityQuery['directBioentity'], 'MoleculeNode'>;

type SequenceInfo = ExtractByTypename<
  BulkBioentityLiveDataQuery['directBioentities'][number],
  'SequenceNode'
>;

// Keep in sync with benchling/modbio/structured_tables/bases_column_util.py
export enum BasesColumnEntityType {
  DNA_SEQUENCE = 'DNA_SEQUENCE',
  DNA_OLIGO = 'DNA_OLIGO',
  RNA_SEQUENCE = 'RNA_SEQUENCE',
  RNA_OLIGO = 'RNA_OLIGO',
  AA_SEQUENCE = 'AA_SEQUENCE',
}

export interface BasesColumnConfig {
  entityType: BasesColumnEntityType;
  syntax?: Notation | null;
  monomerMappingConfig?: MonomerMapping | null | typeof INVALID_MONOMER_MAPPING_CONFIG;
  customSyntaxName?: string | null;
}

// Used for errors during config migration
export const INVALID_MONOMER_MAPPING_CONFIG = 'INVALID_MONOMER_MAPPING_CONFIG';

export function getBasesColumnConfigDisplayName(basesColumnConfig: BasesColumnConfig): string {
  if (basesColumnConfig.entityType === BasesColumnEntityType.AA_SEQUENCE) {
    return 'Residues';
  } else {
    switch (basesColumnConfig.syntax) {
      case Notation.HELM:
        return BASES_HELM_COLUMN_NAME;
      case Notation.IDT:
        return BASES_IDT_COLUMN_NAME;
      case Notation.NATURAL_BASES:
        return BASES_COLUMN_NAME;
      case Notation.CUSTOM_SYNTAX:
        return `Bases (${basesColumnConfig.customSyntaxName!})`;
      default:
        assert(false, `Unsupported syntax type ${basesColumnConfig.syntax}`);
    }
  }
}

export function getSpreadsheetTypeForBasesColumn(
  structuredTableColumnOrColumnDefinition: StructuredTableColumn | ColumnDefinition
): SpreadsheetBulkImportColumnType {
  assert(
    getColumnTypeFromColumnOrDefinition(structuredTableColumnOrColumnDefinition) === TableColumnType.BASES,
    'Expected table column type to be BASES'
  );
  const basesColumnConfig = isColumnDefinition(structuredTableColumnOrColumnDefinition)
    ? (structuredTableColumnOrColumnDefinition as BasesColumnDefinition).basesColumnConfig
    : (structuredTableColumnOrColumnDefinition as BasesStructuredTableColumn).basesColumnConfig;
  if (basesColumnConfig.entityType === BasesColumnEntityType.AA_SEQUENCE) {
    return SpreadsheetBulkImportColumnType.RESIDUES;
  } else {
    switch (basesColumnConfig.syntax) {
      case Notation.HELM:
        return SpreadsheetBulkImportColumnType.HELM;
      case Notation.IDT:
        return SpreadsheetBulkImportColumnType.IDT;
      case Notation.CUSTOM_SYNTAX:
        return SpreadsheetBulkImportColumnType.CUSTOM_MOD_SYNTAX;
      case Notation.NATURAL_BASES:
        return SpreadsheetBulkImportColumnType.RESIDUES;
      default:
        assert(false, `Unsupported syntax type ${basesColumnConfig.syntax}`);
    }
  }
}

export function getNucleotideTypeForBasesColumnConfig(basesColumnConfig: BasesColumnConfig): NucleotideType {
  switch (basesColumnConfig.entityType) {
    case BasesColumnEntityType.DNA_OLIGO:
    case BasesColumnEntityType.DNA_SEQUENCE:
      return NucleotideType.DNA;
    case BasesColumnEntityType.RNA_OLIGO:
    case BasesColumnEntityType.RNA_SEQUENCE:
      return NucleotideType.RNA;
    default:
      assert(false, `Expected a nucleotide sequence entity type, but got ${basesColumnConfig.entityType}`);
  }
}

export function getSequenceTypeForBasesColumnConfig(basesColumnConfig: BasesColumnConfig): SequenceType {
  switch (basesColumnConfig.entityType) {
    case BasesColumnEntityType.DNA_OLIGO:
    case BasesColumnEntityType.RNA_OLIGO:
      return SequenceType.OLIGO;
    case BasesColumnEntityType.DNA_SEQUENCE:
      return SequenceType.DOUBLE_STRANDED;
    case BasesColumnEntityType.RNA_SEQUENCE:
      return SequenceType.SINGLE_STRANDED;
    default:
      assert(false, `Expected a nucleotide sequence entity type, but got ${basesColumnConfig.entityType}`);
  }
}
export function getBasesValueFromBioentitySummary(
  basesColumnConfig: BasesColumnConfig,
  bioentitySummary:
    | BioentityQueryBasicFolderItemNode
    | BioentityQueryMixtureNode
    | BioentityQueryMoleculeNode
    | BioentityQueryProteinNode
    | BioentityQuerySequenceNode
    | RegisteredEntityInfo
): string | null {
  if (basesColumnConfig.entityType === BasesColumnEntityType.AA_SEQUENCE) {
    if (Object.keys(bioentitySummary).includes('versionJson')) {
      return (bioentitySummary as BioentityQueryProteinNode).versionJson.aminoAcids;
    } else {
      return null;
    }
  } else {
    switch (basesColumnConfig.syntax) {
      case Notation.HELM:
        if (Object.keys(bioentitySummary).includes('helmString')) {
          return (bioentitySummary as BioentityQuerySequenceNode).helmString;
        }
        break;
      case Notation.IDT:
        if (Object.keys(bioentitySummary).includes('idtString')) {
          return (bioentitySummary as BioentityQuerySequenceNode).idtString;
        }
        break;
      case Notation.CUSTOM_SYNTAX:
        if (Object.keys(bioentitySummary).includes('customSyntaxString')) {
          return (bioentitySummary as BioentityQuerySequenceNode).customSyntaxString;
        }
        break;
      case Notation.NATURAL_BASES:
        if (Object.keys(bioentitySummary).includes('bases')) {
          return getUTFlexibleNaturalBasesIfEnabled(
            (bioentitySummary as SequenceInfo).bases,
            (bioentitySummary as SequenceInfo).modifiedNucleotides ?? [],
            getNucleotideTypeForBasesColumnConfig(basesColumnConfig),
            getSequenceTypeForBasesColumnConfig(basesColumnConfig)
          );
        }
        break;
      default:
    }
    return null;
  }
}

const SEQUENCE_FEATURE_TO_STRING: {[key in SequenceFeature]: string} = {
  [SequenceFeature.ANNOTATIONS]: 'annotations',
  [SequenceFeature.CHEMICAL_MODIFICATIONS]: 'chemical modifications',
  [SequenceFeature.PARTS]: 'parts',
  [SequenceFeature.PRIMERS]: 'primers',
  [SequenceFeature.TRANSLATIONS]: 'translations',
};

function resolveCustomSyntaxValue(
  formattedValue: string,
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  nucleotideType: NucleotideType,
  customSyntaxName: string
): ResolvedValueInfo {
  const query: ResolutionQuery = {
    customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_CUSTOM_SYNTAX,
    customSyntaxString: formattedValue,
    nucleotideType,
    customSyntaxName,
    type: ResolutionQueryType.CUSTOM,
  };

  const {error} = resolveFromResolutionQuery(typedValue, query, makeTypedValueFromCustomResolvedValue);
  return {
    resolvedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: formattedValue,
    },
    error,
  };
}

function resolveAASequenceValue(formattedValue: string): ResolvedValueInfo {
  const error = !areAminoAcidsValid(formattedValue)
    ? new ValueError(`${ellipsize(formattedValue, 20)} is not a valid protein sequence.`)
    : null;
  return {
    resolvedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: formattedValue,
    },
    error,
  };
}

function resolveSequenceBasesValue(
  formattedValue: string,
  resolveFromResolutionQuery: ResolveQueryFn,
  typedValue: TypedValue,
  columnDefinition: BasesColumnDefinition
): ResolvedValueInfo {
  const strippedValue = formattedValue.replace(/\s+/g, '');
  const basesColumnConfig = columnDefinition.basesColumnConfig;

  if (basesColumnConfig.entityType === BasesColumnEntityType.AA_SEQUENCE) {
    return resolveAASequenceValue(strippedValue);
  }

  const forOligo = [BasesColumnEntityType.DNA_OLIGO, BasesColumnEntityType.RNA_OLIGO].includes(
    basesColumnConfig.entityType
  );
  const nucleotideType = [BasesColumnEntityType.RNA_OLIGO, BasesColumnEntityType.RNA_SEQUENCE].includes(
    basesColumnConfig.entityType
  )
    ? NucleotideType.RNA
    : NucleotideType.DNA;
  switch (basesColumnConfig.syntax) {
    case Notation.NATURAL_BASES:
      return resolveNaturalBasesValue(strippedValue, nucleotideType, SEQUENCE_MAX_LENGTH, forOligo);
    case Notation.HELM:
    case Notation.IDT:
      return resolveHELMOrIDTValue(
        strippedValue,
        resolveFromResolutionQuery,
        typedValue,
        nucleotideType,
        basesColumnConfig.syntax
      );
    case Notation.CUSTOM_SYNTAX:
      return resolveCustomSyntaxValue(
        strippedValue,
        resolveFromResolutionQuery,
        typedValue,
        nucleotideType,
        basesColumnConfig.customSyntaxName!
      );
    default:
      assert(false, 'Unrecognized notation');
  }
}

export function getExistingFeaturesStringFromBioentitySummary(
  basesColumnConfig: BasesColumnConfig,
  bioentitySummary:
    | BioentityQueryBasicFolderItemNode
    | BioentityQueryMixtureNode
    | BioentityQueryMoleculeNode
    | BioentityQueryProteinNode
    | BioentityQuerySequenceNode
    | RegisteredEntityInfo
): string | null {
  let existingFeaturesString = null;
  const {entityType} = basesColumnConfig;
  if (entityType === BasesColumnEntityType.AA_SEQUENCE) {
    if (
      Object.keys(bioentitySummary).includes('versionJson') &&
      (bioentitySummary as BioentityQueryProteinNode).versionJson.annotations.length > 0
    ) {
      existingFeaturesString = 'annotations';
    }
  } else {
    const {existingFeatures} = bioentitySummary as BioentityQuerySequenceNode;
    if (existingFeatures.length > 0) {
      existingFeaturesString = existingFeatures.map((feat) => SEQUENCE_FEATURE_TO_STRING[feat]).join(', ');
    }
  }

  if (existingFeaturesString == null) {
    return null;
  }
  let entityTypeString = null;
  if ([BasesColumnEntityType.DNA_SEQUENCE, BasesColumnEntityType.RNA_SEQUENCE].includes(entityType)) {
    entityTypeString = 'sequence';
  } else if (entityType === BasesColumnEntityType.AA_SEQUENCE) {
    entityTypeString = 'protein';
  } else {
    entityTypeString = 'oligo';
  }
  return `If updated, the following features on this ${entityTypeString} would be deleted: ${existingFeaturesString}.`;
}

export function updateBasesColumnMonomerMappingConfig({
  performTableOpOnTable,
  monomerMapping,
}: {
  performTableOpOnTable: ({op, options}: {op: TableOpFunction; options?: TableOpOptions}) => void;
  monomerMapping?: MonomerMapping;
}): void {
  performTableOpOnTable({
    op: (table) => {
      const updatedTable = produce(table, (draftTable) => {
        const structuredTableColumns = draftTable.columnConstraints.map(
          (constraint) => constraint?.structuredTableColumn
        );
        const basesCol = structuredTableColumns.find(
          (structuredTableColumn): structuredTableColumn is BasesStructuredTableColumn =>
            structuredTableColumn?.columnType === TableColumnType.BASES
        );
        if (basesCol != null) {
          basesCol.basesColumnConfig = {
            ...basesCol.basesColumnConfig,
            monomerMappingConfig: monomerMapping,
          };
        }
      });
      return {table: updatedTable, tokenUpdaters: [], box: null, dim: null};
    },
    options: {ignoreLockedDimensions: true},
  });
}

export function getRegistrationTableSettingsModalPropsForMonomerMapping(
  columns: Array<StructuredTableColumn>
): {
  monomerMapping: MonomerMapping | typeof INVALID_MONOMER_MAPPING_CONFIG | null | undefined;
  showMonomerMappingForm: boolean;
} {
  const basesColumn = columns.find(
    (column): column is BasesStructuredTableColumn => column.columnType === TableColumnType.BASES
  );
  const monomerMapping = basesColumn?.basesColumnConfig.monomerMappingConfig;
  const showMonomerMappingForm =
    BenchlingConfig.MODBIO_ENABLE_SEQUENCES_IN_REG_TABLES &&
    (basesColumn?.basesColumnConfig.entityType === BasesColumnEntityType.RNA_SEQUENCE ||
      basesColumn?.basesColumnConfig.entityType === BasesColumnEntityType.RNA_OLIGO) &&
    basesColumn.basesColumnConfig.syntax === Notation.NATURAL_BASES;
  return {monomerMapping, showMonomerMappingForm};
}

/**
 * Custom eval function to validate the input for nucleotide and AA sequences.
 * If the table type is REGISTER_EXISTING_ENTITIES, this function will also compare the typedValue residues
 * input against the existing entity's residues, and provide a warning if they differ that features such as
 * annotations and modified nucleotides will get overwritten by the registration action.
 * @param typedValue - the typedValue of the current cell
 * @param columnDefinition - the current column the typedValue is in
 * @param cellDependencies - an array containing the existing bioentity for the row
 * @param metadata - the Registration table's metadata
 */
export function basesColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: BasesColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
  }: {
    tableMetadata: RegistrationStructuredTableMetadata;
    rowMetadata: RegistrationStructuredTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const formattedValue = formatTypedValue(typedValue);
  let sequenceOverwriteWarning = null;
  if (
    tableMetadata.insertType === RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES &&
    cellDependencies.length > 0
  ) {
    const bioentityTypedValue = cellDependencies[0];
    if (bioentityTypedValue && bioentityTypedValue.type === BaseTypedValueType.BIOENTITY) {
      const {resolvedValue, error} = resolveFromResolutionQuery(
        typedValue,
        {
          type: ResolutionQueryType.CUSTOM,
          customResolutionQueryType:
            CustomResolutionQueryType.REGISTRATION_TABLE_SCHEMA_TAGS_FROM_EXISTING_BIOENTITY,
          bioentityId: bioentityTypedValue.bioentitySummary.id,
          columnDefinition,
          typedValue,
        },
        makeTypedValueFromCustomResolvedValue
      );
      if (error == null && isTypedValueEmpty(typedValue)) {
        // If the typedValue input is empty, we show the existing sequence's bases as lookupTagValue does
        return {val: resolvedValue, error: null, locked: false};
      } else if (error != null) {
        // If we're in "Register existing entities" mode and the bases input doesn't match up with the existing
        // entity, we warn the user that bases will be updated and sequence features will be overwritten.
        sequenceOverwriteWarning = error;
      }
    }
  }
  const {error: basesError} = resolveSequenceBasesValue(
    formattedValue,
    resolveFromResolutionQuery,
    typedValue,
    columnDefinition
  );

  return {val: typedValue, error: basesError ?? sequenceOverwriteWarning, locked: false};
}
